// shell/components/table/column-manager.ts

import { IDisposable } from '../types';
import { HeatMapShader, HeatMapBounds } from '../heat-map';
import { DataSource } from '../data-source';
import { ColumnDef, ColumnState } from './types';

export interface ColumnManagerConfig<T> {
    columns: ColumnDef<T>[];
    persistenceKey?: string;
    onVisibilityChange?: (visibleKeys: string[]) => void;
    onOrderChange?: (keys: string[]) => void;
}

/**
 * Cache key for heat map bounds includes a data version.
 */
interface BoundsCacheEntry {
    bounds: HeatMapBounds;
    dataVersion: number;
}

/**
 * Manages column definitions, visibility, ordering, responsive hiding, and heat map bounds.
 */
export class ColumnManager<T> implements IDisposable {
    private readonly columns: ColumnDef<T>[];
    private readonly columnsByKey: Map<string, ColumnDef<T>>;
    private visibleKeys: Set<string>;
    private columnOrder: string[];
    private responsiveHiddenKeys: Set<string> = new Set();

    /** Cached responsive columns sorted by priority (invalidated when columns change) */
    private responsiveSortedCache: ColumnDef<T>[] | null = null;

    /** Heat map bounds cache with data version tracking */
    private readonly boundsCache: Map<string, BoundsCacheEntry> = new Map();
    private dataVersion = 0;

    /** Shared HeatMapShader for bounds computation */
    private readonly heatMapShader = new HeatMapShader();

    /** Flag to prevent redundant saves during state restoration */
    private restoringState = false;

    constructor(private config: ColumnManagerConfig<T>) {
        this.columns = [...config.columns];

        // Validate column key uniqueness
        const seenKeys = new Set<string>();
        for (const col of this.columns) {
            if (seenKeys.has(col.key)) {
                console.warn(`Duplicate column key: "${col.key}" - later definition will override`);
            }
            seenKeys.add(col.key);
        }

        this.columnsByKey = new Map(this.columns.map(c => [c.key, c]));

        // Initialize visibility
        this.visibleKeys = new Set(
            this.columns
                .filter(c => !c.defaultHidden)
                .map(c => c.key)
        );

        // Initialize order
        this.columnOrder = this.columns.map(c => c.key);

        // Restore persisted state
        this.restoreState();
    }

    // === Column access ===

    getAllColumns(): ColumnDef<T>[] {
        return this.columnOrder
            .map(key => this.columnsByKey.get(key))
            .filter((c): c is ColumnDef<T> => c != null);
    }

    getVisibleColumns(): ColumnDef<T>[] {
        return this.getAllColumns().filter(c =>
            this.visibleKeys.has(c.key) && !this.responsiveHiddenKeys.has(c.key)
        );
    }

    getColumnByKey(key: string): ColumnDef<T> | undefined {
        return this.columnsByKey.get(key);
    }

    // === Visibility ===

    isVisible(key: string): boolean {
        return this.visibleKeys.has(key) && !this.responsiveHiddenKeys.has(key);
    }

    setColumnVisibility(key: string, visible: boolean): void {
        const col = this.columnsByKey.get(key);
        if (!col) return;

        // Cannot hide alwaysShow columns
        if (!visible && col.alwaysShow) return;

        if (visible) {
            this.visibleKeys.add(key);
        } else {
            this.visibleKeys.delete(key);
        }

        this.saveState();
        this.config.onVisibilityChange?.(Array.from(this.visibleKeys));
    }

    setVisibleColumns(keys: string[]): void {
        // Always include alwaysShow columns
        const alwaysShow = this.columns
            .filter(c => c.alwaysShow)
            .map(c => c.key);

        this.visibleKeys = new Set([...alwaysShow, ...keys]);
        this.saveState();
        this.config.onVisibilityChange?.(Array.from(this.visibleKeys));
    }

    /**
     * Batch update column visibility.
     * More efficient than multiple setColumnVisibility calls - single save and callback.
     */
    setColumnsVisibility(changes: Map<string, boolean>): void {
        let changed = false;

        for (const [key, visible] of changes) {
            const col = this.columnsByKey.get(key);
            if (!col) continue;

            // Cannot hide alwaysShow columns
            if (!visible && col.alwaysShow) continue;

            if (visible && !this.visibleKeys.has(key)) {
                this.visibleKeys.add(key);
                changed = true;
            } else if (!visible && this.visibleKeys.has(key)) {
                this.visibleKeys.delete(key);
                changed = true;
            }
        }

        if (changed) {
            this.saveState();
            this.config.onVisibilityChange?.(Array.from(this.visibleKeys));
        }
    }

    // === Ordering ===

    getColumnOrder(): string[] {
        return [...this.columnOrder];
    }

    setColumnOrder(keys: string[]): void {
        // Validate all keys exist
        const validKeys = keys.filter(k => this.columnsByKey.has(k));

        // Add any missing keys at the end
        const missingKeys = this.columns
            .map(c => c.key)
            .filter(k => !validKeys.includes(k));

        this.columnOrder = [...validKeys, ...missingKeys];
        this.saveState();
        this.config.onOrderChange?.(this.columnOrder);
    }

    moveColumn(key: string, toIndex: number): void {
        const fromIndex = this.columnOrder.indexOf(key);
        if (fromIndex === -1) return;

        this.columnOrder.splice(fromIndex, 1);
        this.columnOrder.splice(toIndex, 0, key);

        this.saveState();
        this.config.onOrderChange?.(this.columnOrder);
    }

    // === Responsive hiding ===

    /**
     * Get responsive columns sorted by priority (cached).
     */
    private getResponsiveSorted(): ColumnDef<T>[] {
        if (!this.responsiveSortedCache) {
            this.responsiveSortedCache = this.columns
                .filter(c => c.responsiveHide && !c.alwaysShow)
                .sort((a, b) => (b.responsiveHide!.priority - a.responsiveHide!.priority));
        }
        return this.responsiveSortedCache;
    }

    updateForContainerWidth(width: number): void {
        const toHide: string[] = [];
        const responsive = this.getResponsiveSorted();

        for (const col of responsive) {
            if (width < col.responsiveHide!.minContainerWidth) {
                toHide.push(col.key);
            }
        }

        this.responsiveHiddenKeys = new Set(toHide);
    }

    getHiddenByResponsive(): string[] {
        return Array.from(this.responsiveHiddenKeys);
    }

    // === Heat map ===

    /**
     * Notify that data has changed - invalidates all heat map caches.
     * Call this when items are added, removed, or modified.
     */
    notifyDataChanged(): void {
        this.dataVersion++;
    }

    /**
     * Compute heat map bounds for all heat-map-enabled columns in a single pass.
     * More efficient than computing per-column when multiple columns need bounds.
     */
    computeAllHeatMapBounds(items: T[]): void {
        const heatMapColumns = this.columns.filter(c => c.heatMap?.enabled);
        if (heatMapColumns.length === 0 || items.length === 0) return;

        // Collect values for all columns in single pass
        const columnValues = new Map<string, number[]>();
        const errorCounts = new Map<string, number>();
        for (const col of heatMapColumns) {
            columnValues.set(col.key, []);
            errorCounts.set(col.key, 0);
        }

        for (const item of items) {
            for (const col of heatMapColumns) {
                const valueFn = col.heatMap!.valueFn ?? col.getValue;
                try {
                    const raw = valueFn(item);
                    const rank = this.rankValue(raw);
                    if (rank != null) {
                        columnValues.get(col.key)!.push(rank);
                    }
                } catch (e) {
                    // Track error count per column
                    errorCounts.set(col.key, errorCounts.get(col.key)! + 1);
                }
            }
        }

        // Log warnings for columns with errors
        for (const [key, count] of errorCounts) {
            if (count > 0) {
                console.warn(`Heat map column "${key}": ${count} items skipped due to value extraction errors`);
            }
        }

        // Compute bounds for each column
        for (const col of heatMapColumns) {
            const values = columnValues.get(col.key)!;
            if (values.length > 0) {
                const bounds = this.heatMapShader.computeBounds(values);
                if (bounds) {
                    this.boundsCache.set(col.key, {
                        bounds,
                        dataVersion: this.dataVersion
                    });
                }
            }
        }
    }

    /**
     * Compute heat map bounds from a DataSource for a visible range.
     * More efficient than computeAllHeatMapBounds when only visible items matter.
     *
     * @param dataSource - The data source to read items from
     * @param startIndex - Start of visible range (inclusive)
     * @param endIndex - End of visible range (exclusive)
     */
    computeHeatMapBoundsFromDataSource(
        dataSource: DataSource<T>,
        startIndex: number,
        endIndex: number
    ): void {
        const items = dataSource.getItems(startIndex, endIndex);
        // Filter out undefined items (not yet loaded)
        const loadedItems = items.filter((item): item is T => item != null);
        if (loadedItems.length > 0) {
            this.computeAllHeatMapBounds(loadedItems);
        }
    }

    /**
     * Compute heat map bounds for a single column.
     * Uses cache if data version matches.
     */
    computeHeatMapBounds(key: string, items: T[]): HeatMapBounds | undefined {
        // Check cache with version
        const cached = this.boundsCache.get(key);
        if (cached && cached.dataVersion === this.dataVersion) {
            return cached.bounds;
        }

        const col = this.getColumnByKey(key);
        if (!col?.heatMap?.enabled) return undefined;

        const valueFn = col.heatMap.valueFn ?? col.getValue;
        const values: number[] = [];
        let errorCount = 0;

        for (const item of items) {
            try {
                const raw = valueFn(item);
                const rank = this.rankValue(raw);
                if (rank != null) values.push(rank);
            } catch (e) {
                errorCount++;
            }
        }

        if (errorCount > 0) {
            console.warn(`Heat map column "${key}": ${errorCount} items skipped due to value extraction errors`);
        }

        if (values.length === 0) return undefined;

        const bounds = this.heatMapShader.computeBounds(values);
        if (bounds) {
            this.boundsCache.set(key, {
                bounds,
                dataVersion: this.dataVersion
            });
        }
        return bounds ?? undefined;
    }

    /**
     * Get cached heat map bounds for a column.
     * Returns undefined if cache is stale or missing.
     */
    getHeatMapBounds(key: string): HeatMapBounds | undefined {
        const cached = this.boundsCache.get(key);
        if (cached && cached.dataVersion === this.dataVersion) {
            return cached.bounds;
        }
        return undefined;
    }

    /**
     * Explicitly invalidate heat map bounds cache.
     * @param key - Specific column key, or undefined to clear all
     */
    invalidateHeatMapBounds(key?: string): void {
        if (key) {
            this.boundsCache.delete(key);
        } else {
            this.boundsCache.clear();
        }
    }

    /**
     * Rank a value for heat map comparison.
     * Delegates to HeatMapShader's static ranking methods.
     */
    private rankValue(value: unknown): number | null {
        if (value == null) return null;

        if (typeof value === 'number') {
            return HeatMapShader.rankNumber(value);
        }

        if (value instanceof Date) {
            return HeatMapShader.rankDate(value);
        }

        if (typeof value === 'string') {
            // Check if it's an IP address
            if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(value)) {
                return HeatMapShader.rankIpAddress(value);
            }
            return HeatMapShader.rankString(value);
        }

        return null;
    }

    // === Persistence ===

    saveState(): void {
        // Skip saves during state restoration to avoid redundant writes
        if (this.restoringState || !this.config.persistenceKey) return;

        const state: ColumnState = {
            visibleKeys: Array.from(this.visibleKeys),
            order: this.columnOrder
        };

        try {
            localStorage.setItem(this.config.persistenceKey, JSON.stringify(state));
        } catch (e) {
            console.warn('Failed to save column state:', e);
        }
    }

    restoreState(): boolean {
        if (!this.config.persistenceKey) return false;

        try {
            const json = localStorage.getItem(this.config.persistenceKey);
            if (!json) return false;

            const state: ColumnState = JSON.parse(json);

            // Set flag to prevent redundant saves during restoration
            this.restoringState = true;
            try {
                this.setState(state);
            } finally {
                this.restoringState = false;
            }

            return true;
        } catch (e) {
            console.warn('Failed to restore column state:', e);
            return false;
        }
    }

    getState(): ColumnState {
        return {
            visibleKeys: Array.from(this.visibleKeys),
            order: [...this.columnOrder]
        };
    }

    setState(state: ColumnState): void {
        // Validate and apply visibility
        const validVisible = state.visibleKeys.filter(k => this.columnsByKey.has(k));
        const alwaysShow = this.columns.filter(c => c.alwaysShow).map(c => c.key);
        this.visibleKeys = new Set([...alwaysShow, ...validVisible]);

        // Validate and apply order
        this.setColumnOrder(state.order);
    }

    /**
     * Invalidate cached sorts when columns change.
     * Call this if columns are dynamically added/removed.
     */
    invalidateColumnCaches(): void {
        this.responsiveSortedCache = null;
    }

    dispose(): void {
        this.boundsCache.clear();
        this.responsiveSortedCache = null;
    }
}
